import Jumbotron from '@/components/core/Jumbotron/Jumbotron';
import Section from '@/components/core/Section/Section';
import SectionColumn from '@/components/core/Section/SectionColumn/SectionColumn';
import SectionItem from '@/components/core/Section/SectionItem/SectionItem';
import NewsBox from '@/components/features/book/NewsBox/NewsBox';
import Quote from '@/components/features/book/Quote/Quote';
import { Layout } from '@/components/shared/layouts/Layout/Layout';
import { ColorTypeOptions } from '@/components/shared/navigations/CoreNavigation/CoreNavigation';
import { getSafeLocale } from '@/utils/locale';
import { GetStaticProps, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import styles from '../styles/book.module.scss';

const BookPage: NextPage = () => {
	const { t } = useTranslation();

	return (
		<>
			<NextSeo title={t('museum-book')} />
			<Layout colorTypeActive={ColorTypeOptions.PRIMARY}>
				<Jumbotron
					maskClassName={styles.museumBookJumbotronMask}
					backgroundImage="/images/museum-book-header-background.jpg"
					className={styles.museumBookJumbotron}
				></Jumbotron>
				<Section color="Primary">
					<div className={styles.museumBookNewsMobile}>
						<NewsBox
							type="news"
							title={t('modern-love-in-203-everyday-ob')}
							date={t('nov-14-2017')}
							place={t('worldwide')}
						>
							{`${t('a-wedding-dress-sealed-in-a-ja')} `}
							<a href="https://www.brokenships.store/products/the-museum-of-broken-relationships" target="_blank">{`${t(
								'the-museum-of-broken-relations'
							)} `}</a>
							{t('our-gorgeous-book-appearing')}
						</NewsBox>
					</div>
					<SectionColumn title={t('publisher-and-authors')}>
						<SectionItem title={t('publisher')} icon="/assets/icons/ic-publisher.svg">
							<ul className={styles.list}>
								<li className={styles.listItem}>
									US -&nbsp;
									<a
										href="https://www.hachettebookgroup.com/titles/olinka-vistica/the-museum-of-broken-relationships/9781478970507/"
										target="_blank"
									>
										{t('grand-central-publishing-hach')}
									</a>
								</li>
								<li className={styles.listItem}>
									UK -&nbsp;
									<a href="https://www.orionbooks.co.uk/books/detail.page?isbn=9781474605496" target="_blank">
										{t('weidenfeld--nicolson-orion-b')}
									</a>
								</li>
							</ul>
							<p>
								{`${t('for-dramatic-rights-inquiries')} `}
								<a href="mailto:tfk@cbltd.com">{t('timothy-knowlton')}</a> {t('at')}{' '}
								<a href="https://www.curtisbrown.com/" target="_blank">
									{t('curtis-brown')}
								</a>
								.<br />
								{`${t('for-translation-rights-please')} `}
								<a href="mailto:Nicole.Bond@hbgusa.com">Nicole Bond</a> {t('at')}{' '}
								<a href="https://www.hachettebookgroup.com/imprint/grand-central-publishing" target="_blank">
									{t('grand-central')}
								</a>
								.<br />
								{`${t('for-publicity-inquires-please')} `}{' '}
								<a href="mailto:Linda.Duggins@hbgusa.com">{t('linda-duggins')}</a> {t('at')}{' '}
								<a href="https://www.hachettebookgroup.com/imprint/grand-central-publishing" target="_blank">
									{t('grand-central')}
								</a>
								.<br />
							</p>
						</SectionItem>
						<SectionItem title={t('authors')} icon="/assets/icons/ic-author.svg">
							<p>
								<b>Olinka Vištica</b>
								{` ${t('is-an-arts-producer-born-in-s')}`}
							</p>
							<p>
								<b>Dražen Grubišić</b>
								{` ${t('is-a-versatile-visual-artist-f')}`}
							</p>
						</SectionItem>
					</SectionColumn>
					<SectionColumn showTitle={false}>
						<div className={styles.museumBookNewsDesktop}>
							<NewsBox
								type="news"
								title={t('modern-love-in-203-everyday-ob')}
								date={t('nov-14-2017')}
								place={t('worldwide')}
							>
								{`${t('a-wedding-dress-sealed-in-a-ja')} `}
								<a
									href="https://www.brokenships.store/products/the-museum-of-broken-relationships"
									target="_blank"
								>{`${t('the-museum-of-broken-relations')} `}</a>
								{t('our-gorgeous-book-appearing')}
							</NewsBox>
						</div>
						<SectionItem title={t('book-shop')} icon={'/assets/icons/ic-shop.svg'}>
							<ul className={styles.list}>
								<li className={styles.listItem}>
									<a
										href="https://www.hachettebookgroup.com/titles/olinka-vistica/the-museum-of-broken-relationships/9781478970507/"
										target="_blank"
									>
										Hachette book group
									</a>
								</li>
								<li className={styles.listItem}>
									<a
										href="https://www.amazon.com/Museum-Broken-Relationships-Olinka-Vistica/dp/1478970502"
										target="_blank"
									>
										Amazon
									</a>
								</li>
								<li className={styles.listItem}>
									<a
										href="https://www.barnesandnoble.com/w/museum-of-broken-relationships-olinka-vistica/1126007460"
										target="_blank"
									>
										Barns and Noble
									</a>
								</li>
								<li className={styles.listItem}>
									<a href="https://www.indiebound.org/book/9781478970507" target="_blank">
										Indiebound
									</a>
								</li>
								<li className={styles.listItem}>
									<a
										href="http://www.booksamillion.com/p/Museum-Broken-Relationships/Olinka-Vistica/9781478970507"
										target="_blank"
									>
										Books a million
									</a>
								</li>
							</ul>
						</SectionItem>
					</SectionColumn>
				</Section>
				<Section color="Dimmed">
					<SectionColumn showTitle={false}>
						<SectionItem>
							<Quote author={t('frank-warren-new-york-times-b')}>{t('this-menagerie-of-heartfelt-c')}</Quote>
						</SectionItem>
					</SectionColumn>
					<SectionColumn showTitle={false}>
						<SectionItem>
							<Quote author={t('colum-mccann-national-book-aw')}>{t('if-all-the-world-is-a-museum-o')}</Quote>
						</SectionItem>
					</SectionColumn>
				</Section>
			</Layout>
		</>
	);
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
	return {
		props: {
			...(await serverSideTranslations(getSafeLocale(locale), ['common'])),
		},
	};
};

export default BookPage;
